import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import api, { formatApiError } from "@/lib/api";
import { eur } from "@/lib/constants";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import {
  Euro,
  Bell,
  Users,
  Wrench,
  Clock,
  TrendingUp,
  ClipboardCheck,
  Loader2,
  Percent,
  Receipt,
  UserPlus,
  Activity,
  MapPin,
  Plus,
  Building2,
  CheckCircle2,
  Circle,
  ArrowRight,
  CalendarDays,
  AlertTriangle,
  Search,
  X,
  WalletCards,
  PackageOpen,
  Boxes,
  FileCheck2,
} from "lucide-react";


/*
 * Si alguna ruta no coincide con App.js,
 * solo tendrás que cambiarla aquí.
 */
const ROUTES = {
  company: "/empresa",
  clients: "/clientes",
  technicians: "/tecnicos",
  workOrders: "/ordenes",
  alerts: "/avisos",
  calendar: "/calendario",
  map: "/mapa",
  quotes: "/quotes",
  invoices: "/facturas",
  inventory: "/inventario",
  suppliers: "/proveedores",
  purchases: "/compras",
  maintenance: "/mantenimiento",
};

const SEARCH_ITEMS = [
  {
    label: "Panel",
    description: "Resumen general de la empresa",
    keywords: ["inicio", "dashboard", "panel"],
    route: "/",
  },
  {
    label: "Avisos",
    description: "Incidencias y solicitudes de clientes",
    keywords: ["aviso", "incidencia", "urgente"],
    route: ROUTES.alerts,
  },
  {
    label: "Órdenes de trabajo",
    description: "Trabajos, partes y estados",
    keywords: ["orden", "trabajo", "parte"],
    route: ROUTES.workOrders,
  },
  {
    label: "Clientes",
    description: "Fichas, direcciones e historial",
    keywords: ["cliente", "empresa", "contacto"],
    route: ROUTES.clients,
  },
  {
    label: "Técnicos",
    description: "Personal, disponibilidad y carga",
    keywords: ["tecnico", "empleado", "personal"],
    route: ROUTES.technicians,
  },
  {
    label: "Calendario",
    description: "Planificación de trabajos",
    keywords: ["calendario", "agenda", "fecha"],
    route: ROUTES.calendar,
  },
  {
    label: "Mapa operativo",
    description: "Ubicación de clientes, técnicos y órdenes",
    keywords: ["mapa", "ruta", "ubicacion"],
    route: ROUTES.map,
  },
  {
    label: "Presupuestos",
    description: "Crear, enviar y convertir presupuestos",
    keywords: ["presupuesto", "oferta", "quote"],
    route: ROUTES.quotes,
  },
  {
    label: "Facturas",
    description: "Cobros, vencimientos y facturación",
    keywords: ["factura", "cobro", "pago", "vencida"],
    route: ROUTES.invoices,
  },
  {
    label: "Inventario",
    description: "Stock, materiales y movimientos",
    keywords: ["inventario", "stock", "material", "producto"],
    route: ROUTES.inventory,
  },
  {
    label: "Proveedores",
    description: "Proveedores y contactos de compra",
    keywords: ["proveedor", "compras", "suministro"],
    route: ROUTES.suppliers,
  },
  {
    label: "Compras",
    description: "Pedidos de compra y recepciones de stock",
    keywords: ["compras", "pedido", "proveedor", "recepcion"],
    route: ROUTES.purchases,
  },
  {
    label: "Mantenimiento",
    description: "Planes preventivos y próximas revisiones",
    keywords: ["mantenimiento", "preventivo", "revision", "equipo"],
    route: ROUTES.maintenance,
  },
  {
    label: "Empresa",
    description: "Datos fiscales, contacto y logotipo",
    keywords: ["empresa", "cif", "nif", "logo", "configuracion"],
    route: ROUTES.company,
  },
];

function Metric({
  icon: Icon,
  label,
  value,
  sub,
  color,
  delay = "",
}) {
  return (
    <div className={`mantix-card animate-fade-up p-6 ${delay}`}>
      <div className="mb-4 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#86868B]">
          {label}
        </span>

        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon
            className="h-[18px] w-[18px]"
            style={{ color }}
            strokeWidth={1.8}
          />
        </div>
      </div>

      <p className="font-display text-3xl font-bold tracking-tight text-[#1D1D1F]">
        {value}
      </p>

      {sub && <p className="mt-1 text-sm text-[#86868B]">{sub}</p>}
    </div>
  );
}

function QuickAction({
  icon: Icon,
  label,
  onClick,
  primary = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        primary
          ? "flex items-center justify-center gap-2 rounded-full bg-[#0066CC] px-5 py-3 text-sm font-medium text-white shadow-[0_8px_20px_rgba(0,102,204,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#004499] focus:outline-none focus:ring-4 focus:ring-[#0066CC]/20"
          : "flex items-center justify-center gap-2 rounded-full border border-[#D2D2D7] bg-white px-5 py-3 text-sm font-medium text-[#1D1D1F] transition-all hover:-translate-y-0.5 hover:bg-[#F5F5F7] focus:outline-none focus:ring-4 focus:ring-black/5"
      }
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
      {label}
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [company, setCompany] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [inventorySummary, setInventorySummary] = useState(null);
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const miniRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const loadDashboard = async () => {
      try {
        const [
          statsResponse,
          mapResponse,
          organizationResponse,
          invoicesResponse,
          inventorySummaryResponse,
          inventoryProductsResponse,
        ] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/map/data"),
          api.get("/organization"),
          api.get("/invoices"),
          api.get("/inventory/summary"),
          api.get("/inventory/products"),
        ]);

        if (!mounted) return;

        setStats(statsResponse.data);
        setMapData(mapResponse.data);
        setCompany(organizationResponse.data);
        setInvoices(invoicesResponse.data || []);
        setInventorySummary(
          statsResponse.data?.inventory || inventorySummaryResponse.data || {}
        );
        setInventoryProducts(
          statsResponse.data?.inventory?.low_stock_products || inventoryProductsResponse.data || []
        );

      } catch (error) {
        if (!mounted) return;

        setLoadError(
          formatApiError(error) ||
            "No se pudo cargar el panel de control."
        );
      }
    };

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!mapData || !stats || miniRef.current) return;

    const element = document.getElementById("mini-map");

    if (!element) return;

    const map = L.map("mini-map", {
      zoomControl: false,
      attributionControl: false,
    }).setView([40.4168, -3.7038], 5);

    L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        maxZoom: 19,
      }
    ).addTo(map);

    const points = [];

    const addMarkers = (items, color) => {
      (items || []).forEach((item) => {
        if (item.lat == null || item.lng == null) return;

        L.circleMarker([item.lat, item.lng], {
          radius: 6,
          color: "#FFFFFF",
          weight: 2,
          fillColor: color,
          fillOpacity: 1,
        }).addTo(map);

        points.push([item.lat, item.lng]);
      });
    };

    addMarkers(mapData.clients, "#0066CC");
    addMarkers(mapData.technicians, "#34C759");
    addMarkers(mapData.work_orders, "#FF9500");

    if (points.length > 0) {
      try {
        map.fitBounds(points, {
          padding: [30, 30],
          maxZoom: 11,
        });
      } catch {
        // Se mantiene la vista predeterminada.
      }
    }

    miniRef.current = map;

    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 250);

    return () => {
      clearTimeout(timer);

      if (miniRef.current) {
        miniRef.current.remove();
        miniRef.current = null;
      }
    };
  }, [mapData, stats]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }

      if (event.key === "Escape") {
        setSearchOpen(false);
        setSearchQuery("");
      }
    };

    window.addEventListener("keydown", handleShortcut);

    return () => {
      window.removeEventListener("keydown", handleShortcut);
    };
  }, []);

  if (loadError) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center p-8">
        <div className="mantix-card max-w-md p-8 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-[#FF9500]" />

          <h2 className="mt-4 font-display text-xl font-bold text-[#1D1D1F]">
            No se pudo cargar el panel
          </h2>

          <p className="mt-2 text-sm text-[#86868B]">
            {loadError}
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 rounded-full bg-[#0066CC] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#004499]"
          >
            Volver a intentar
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-[#0066CC]" />

          <p className="mt-3 text-sm text-[#86868B]">
            Preparando tu panel…
          </p>
        </div>
      </div>
    );
  }

  const onboardingSteps = [
    {
      label: "Completar los datos de empresa",
      completed: Boolean(
        (company?.name || company?.company_name || company?.org_name) &&
        (company?.tax_id || company?.cif || company?.nif) &&
        (company?.phone || company?.telefono) &&
        (company?.address || company?.direccion) &&
        company?.email
      ),
      action: () => navigate(ROUTES.company),
    },
    {
      label: "Crear el primer cliente",
      completed: Number(stats.clients_count || 0) > 0,
      action: () => navigate(ROUTES.clients),
    },
    {
      label: "Añadir el primer técnico",
      completed: Number(stats.techs_count || 0) > 0,
      action: () => navigate(ROUTES.technicians),
    },
    {
      label: "Crear la primera orden",
      completed: Number(stats.work_orders_total || 0) > 0,
      action: () => navigate(ROUTES.workOrders),
    },
  ];

  const completedSteps = onboardingSteps.filter(
    (step) => step.completed
  ).length;

  const onboardingProgress = Math.round(
    (completedSteps / onboardingSteps.length) * 100
  );

  const isNewCompany =
    Number(stats.clients_count || 0) === 0 ||
    Number(stats.techs_count || 0) === 0 ||
    Number(stats.work_orders_total || 0) === 0;

  const priorityTotal =
    Object.values(stats.priority_counts || {}).reduce(
      (total, value) => total + Number(value || 0),
      0
    ) || 1;

  const recentOrders = stats.recent_orders || [];
  const revenueSeries = stats.revenue_series || [];

  const invoiceTotals = invoices.reduce(
    (result, invoice) => {
      const total = Number(invoice.total || 0);
      const paid = Number(invoice.paid_amount || 0);
      const pending = Number(
        invoice.pending_amount ??
          Math.max(total - paid, 0)
      );

      result.total += total;
      result.paid += paid;
      result.pending += pending;

      if (invoice.status === "vencida") {
        result.overdue += 1;
        result.overdueAmount += pending;
      }

      if (
        invoice.status === "emitida" ||
        invoice.status === "parcial" ||
        invoice.status === "vencida"
      ) {
        result.pendingCount += 1;
      }

      return result;
    },
    {
      total: 0,
      paid: 0,
      pending: 0,
      overdue: 0,
      overdueAmount: 0,
      pendingCount: 0,
    }
  );

  const lowStockProducts = inventoryProducts
    .filter((product) => product.low_stock)
    .sort(
      (a, b) =>
        Number(a.stock || 0) - Number(b.stock || 0)
    )
    .slice(0, 5);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const searchResults = SEARCH_ITEMS.filter((item) => {
    if (!normalizedSearch) return true;

    const searchableText = [
      item.label,
      item.description,
      ...item.keywords,
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedSearch);
  });

  const openSearchResult = (route) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(route);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      {/* Cabecera */}
      <header className="mb-8 flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-sm font-semibold text-[#0066CC]">
            Panel operativo
          </p>

          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#1D1D1F] sm:text-4xl">
            Buenos días 👋
          </h1>

          <p className="mt-2 max-w-2xl text-[#86868B]">
            Controla lo importante y organiza la jornada de tu empresa
            desde un único lugar.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex min-w-[210px] items-center gap-3 rounded-full border border-[#D2D2D7] bg-white px-5 py-3 text-left text-sm text-[#86868B] transition-all hover:-translate-y-0.5 hover:border-[#B8B8BD] hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-black/5"
            aria-label="Buscar en Navirox"
          >
            <Search className="h-4 w-4 shrink-0 text-[#1D1D1F]" />
            <span className="flex-1">Buscar en Navirox</span>
            <span className="rounded-md bg-[#F5F5F7] px-2 py-0.5 text-[11px] font-medium text-[#86868B]">
              Ctrl K
            </span>
          </button>

          <QuickAction
            icon={Bell}
            label="Nuevo aviso"
            onClick={() => navigate(ROUTES.alerts)}
          />

          <QuickAction
            icon={Users}
            label="Nuevo cliente"
            onClick={() => navigate(ROUTES.clients)}
          />

          <QuickAction
            icon={Plus}
            label="Nueva orden"
            primary
            onClick={() => navigate(ROUTES.workOrders)}
          />
        </div>
      </header>

      {/* Onboarding */}
      {isNewCompany && (
        <section className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div
            className="mantix-card relative overflow-hidden p-7 text-white xl:col-span-2 sm:p-8"
            style={{
              background:
                "linear-gradient(135deg, #071525 0%, #0A315A 55%, #0A6ED1 100%)",
            }}
          >
            <div className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 left-1/3 h-64 w-64 rounded-full bg-[#52C7FF]/20 blur-3xl" />

            <div className="relative flex h-full min-h-[245px] flex-col justify-between gap-8">
              <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#7CC8FF]">
                    Primeros pasos
                  </p>

                  <h2 className="mt-3 max-w-xl font-display text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl">
                    Termina de preparar Navirox
                  </h2>

                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/75">
                    Solo te queda completar la configuración inicial para
                    empezar a gestionar avisos, técnicos y órdenes de trabajo.
                  </p>
                </div>

                <div className="w-fit shrink-0 rounded-3xl border border-white/15 bg-white/10 px-7 py-5 text-center shadow-lg backdrop-blur">
                  <p className="font-display text-4xl font-bold text-white">
                    {onboardingProgress}%
                  </p>

                  <p className="mt-1 text-xs font-medium text-white/70">
                    completado
                  </p>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => {
                    const pendingStep = onboardingSteps.find(
                      (step) => !step.completed
                    );

                    pendingStep?.action();
                  }}
                  className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#0B315C] shadow-md transition-all hover:-translate-y-0.5 hover:bg-[#F5F5F7]"
                >
                  Continuar configuración
                  <ArrowRight className="h-4 w-4" />
                </button>

                <div className="mt-7 h-2.5 overflow-hidden rounded-full bg-white/15">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#4BB6FF] to-[#76E4FF] shadow-[0_0_18px_rgba(118,228,255,0.55)] transition-all duration-700"
                    style={{
                      width: `${onboardingProgress}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mantix-card p-6">
            <div className="mb-5 flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0066CC]/10">
                <Building2 className="h-5 w-5 text-[#0066CC]" />
              </div>

              <div>
                <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                  Configuración inicial
                </h3>

                <p className="text-xs text-[#86868B]">
                  {completedSteps} de {onboardingSteps.length} pasos
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {onboardingSteps.map((step) => (
                <button
                  key={step.label}
                  type="button"
                  onClick={step.action}
                  className="group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-[#F5F5F7]"
                >
                  {step.completed ? (
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-[#34C759]" />
                  ) : (
                    <Circle className="h-5 w-5 shrink-0 text-[#B0B0B5] transition-colors group-hover:text-[#0066CC]" />
                  )}

                  <span
                    className={`flex-1 text-sm ${
                      step.completed
                        ? "text-[#86868B] line-through"
                        : "font-medium text-[#1D1D1F]"
                    }`}
                  >
                    {step.label}
                  </span>

                  <ArrowRight className="h-4 w-4 text-[#B0B0B5] transition-transform group-hover:translate-x-0.5 group-hover:text-[#0066CC]" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {(stats.facturas_vencidas > 0 ||
        stats.inventory?.low_stock > 0 ||
        stats.maintenance?.overdue > 0) && (
        <section className="mb-6 grid grid-cols-1 gap-3 lg:grid-cols-3">
          {stats.facturas_vencidas > 0 && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.invoices)}
              className="flex items-center justify-between rounded-2xl border border-[#FFD7D3] bg-[#FFF5F4] p-4 text-left transition-all hover:-translate-y-0.5"
            >
              <span>
                <span className="block text-sm font-semibold text-[#D70015]">
                  {stats.facturas_vencidas} factura(s) vencida(s)
                </span>
                <span className="mt-1 block text-xs text-[#D70015]/70">
                  {eur(stats.vencido_importe || 0)} pendientes
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-[#D70015]" />
            </button>
          )}

          {stats.inventory?.low_stock > 0 && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.inventory)}
              className="flex items-center justify-between rounded-2xl border border-[#FFE2B8] bg-[#FFF9F0] p-4 text-left transition-all hover:-translate-y-0.5"
            >
              <span>
                <span className="block text-sm font-semibold text-[#C93400]">
                  {stats.inventory.low_stock} producto(s) con stock bajo
                </span>
                <span className="mt-1 block text-xs text-[#C93400]/70">
                  Revisa las reposiciones
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-[#C93400]" />
            </button>
          )}

          {stats.maintenance?.overdue > 0 && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.maintenance)}
              className="flex items-center justify-between rounded-2xl border border-[#FFE2B8] bg-[#FFF9F0] p-4 text-left transition-all hover:-translate-y-0.5"
            >
              <span>
                <span className="block text-sm font-semibold text-[#C93400]">
                  {stats.maintenance.overdue} mantenimiento(s) vencido(s)
                </span>
                <span className="mt-1 block text-xs text-[#C93400]/70">
                  Necesitan planificación
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-[#C93400]" />
            </button>
          )}
        </section>
      )}

      {/* Métricas operativas */}
      <section className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Metric
          icon={Euro}
          label="Facturación del mes"
          value={eur(stats.facturacion_mes || 0)}
          sub={`${stats.facturacion_variacion >= 0 ? "+" : ""}${stats.facturacion_variacion || 0}% vs. mes anterior`}
          color="#34C759"
          delay="stagger-1"
        />

        <Metric
          icon={Bell}
          label="Avisos abiertos"
          value={stats.avisos_abiertos || 0}
          sub={`${stats.avisos_total || 0} en total`}
          color="#FF9500"
          delay="stagger-2"
        />

        <Metric
          icon={Wrench}
          label="Técnicos"
          value={stats.techs_count || 0}
          sub={`${stats.en_curso || 0} órdenes en curso`}
          color="#0066CC"
          delay="stagger-3"
        />

        <Metric
          icon={Clock}
          label="Tiempo medio"
          value={`${stats.avg_time || 0} h`}
          sub="por trabajo completado"
          color="#5856D6"
          delay="stagger-4"
        />
      </section>

      {/* Métricas económicas */}
      <section className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Metric
          icon={TrendingUp}
          label="Beneficio estimado"
          value={eur(stats.beneficio || 0)}
          sub={`${eur(stats.pendiente_cobro || 0)} pendiente de cobro`}
          color="#34C759"
          delay="stagger-1"
        />

        <Metric
          icon={Percent}
          label="Rentabilidad"
          value={`${stats.rentabilidad || 0}%`}
          sub="margen estimado"
          color="#0066CC"
          delay="stagger-2"
        />

        <Metric
          icon={Receipt}
          label="Ticket medio"
          value={eur(stats.ticket_medio || 0)}
          sub={`${stats.resolucion_rate || 0}% de avisos resueltos`}
          color="#FF9500"
          delay="stagger-3"
        />

        <Metric
          icon={UserPlus}
          label="Clientes nuevos"
          value={stats.new_clients_month || 0}
          sub="este mes"
          color="#5856D6"
          delay="stagger-4"
        />
      </section>

      {/* Cobros e inventario */}
      <section className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="mantix-card p-6">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                Cobros y facturas
              </h3>
              <p className="mt-1 text-xs text-[#86868B]">
                Control rápido del dinero pendiente y vencido
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate(ROUTES.invoices)}
              className="flex items-center gap-1.5 text-sm font-medium text-[#0066CC] hover:underline"
            >
              Ver facturas
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              {
                icon: WalletCards,
                label: "Cobrado",
                value: eur(invoiceTotals.paid),
                color: "#34C759",
              },
              {
                icon: Receipt,
                label: "Pendiente",
                value: eur(invoiceTotals.pending),
                color: "#FF9500",
              },
              {
                icon: AlertTriangle,
                label: "Vencidas",
                value: invoiceTotals.overdue,
                color: "#FF3B30",
              },
              {
                icon: FileCheck2,
                label: "Por cobrar",
                value: invoiceTotals.pendingCount,
                color: "#5856D6",
              },
            ].map(({ icon: Icon, label, value, color }) => (
              <div
                key={label}
                className="rounded-2xl bg-[#F5F5F7] p-4"
              >
                <Icon
                  className="mb-3 h-5 w-5"
                  style={{ color }}
                  strokeWidth={1.8}
                />
                <p className="font-display text-xl font-bold text-[#1D1D1F]">
                  {value}
                </p>
                <p className="mt-1 text-xs text-[#86868B]">
                  {label}
                </p>
              </div>
            ))}
          </div>

          {invoiceTotals.overdue > 0 && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.invoices)}
              className="mt-4 flex w-full items-center justify-between rounded-2xl bg-[#FFEBEA] px-4 py-3 text-left"
            >
              <span>
                <span className="block text-sm font-semibold text-[#D70015]">
                  {invoiceTotals.overdue} facturas vencidas
                </span>
                <span className="mt-0.5 block text-xs text-[#D70015]/70">
                  {eur(invoiceTotals.overdueAmount)} pendientes de cobro
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-[#D70015]" />
            </button>
          )}
        </div>

        <div className="mantix-card p-6">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                Estado del inventario
              </h3>
              <p className="mt-1 text-xs text-[#86868B]">
                Stock, alertas y valor del almacén
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate(ROUTES.inventory)}
              className="flex items-center gap-1.5 text-sm font-medium text-[#0066CC] hover:underline"
            >
              Ver inventario
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              {
                icon: Boxes,
                label: "Productos",
                value: inventorySummary?.total_products || 0,
                color: "#0066CC",
              },
              {
                icon: AlertTriangle,
                label: "Stock bajo",
                value: inventorySummary?.low_stock || 0,
                color: "#FF9500",
              },
              {
                icon: PackageOpen,
                label: "Agotados",
                value: inventorySummary?.out_of_stock || 0,
                color: "#FF3B30",
              },
              {
                icon: Euro,
                label: "Valor",
                value: eur(inventorySummary?.stock_value || 0),
                color: "#5856D6",
              },
            ].map(({ icon: Icon, label, value, color }) => (
              <div
                key={label}
                className="rounded-2xl bg-[#F5F5F7] p-4"
              >
                <Icon
                  className="mb-3 h-5 w-5"
                  style={{ color }}
                  strokeWidth={1.8}
                />
                <p className="font-display text-xl font-bold text-[#1D1D1F]">
                  {value}
                </p>
                <p className="mt-1 text-xs text-[#86868B]">
                  {label}
                </p>
              </div>
            ))}
          </div>

          {lowStockProducts.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#86868B]">
                Reponer pronto
              </p>

              {lowStockProducts.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => navigate(ROUTES.inventory)}
                  className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-[#F5F5F7]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-[#1D1D1F]">
                      {product.name}
                    </span>
                    <span className="block text-xs text-[#86868B]">
                      {product.sku}
                    </span>
                  </span>

                  <span className="shrink-0 rounded-full bg-[#FFF4E5] px-2.5 py-1 text-xs font-medium text-[#C93400]">
                    {product.stock} {product.unit}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Mantenimiento preventivo */}
      <section className="mt-6">
        <div className="mantix-card p-6">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                Mantenimiento preventivo
              </h3>
              <p className="mt-1 text-xs text-[#86868B]">
                Próximas revisiones y planes que necesitan atención
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(ROUTES.maintenance)}
              className="flex items-center gap-1.5 text-sm font-medium text-[#0066CC] hover:underline"
            >
              Ver mantenimiento
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-2xl bg-[#F5F5F7] p-4">
              <CalendarDays className="mb-3 h-5 w-5 text-[#0066CC]" />
              <p className="font-display text-xl font-bold text-[#1D1D1F]">
                {stats.maintenance?.active || 0}
              </p>
              <p className="mt-1 text-xs text-[#86868B]">Planes activos</p>
            </div>
            <div className="rounded-2xl bg-[#F5F5F7] p-4">
              <AlertTriangle className="mb-3 h-5 w-5 text-[#FF3B30]" />
              <p className="font-display text-xl font-bold text-[#1D1D1F]">
                {stats.maintenance?.overdue || 0}
              </p>
              <p className="mt-1 text-xs text-[#86868B]">Vencidos</p>
            </div>
            <div className="rounded-2xl bg-[#F5F5F7] p-4">
              <Clock className="mb-3 h-5 w-5 text-[#FF9500]" />
              <p className="font-display text-xl font-bold text-[#1D1D1F]">
                {(stats.maintenance?.upcoming || []).filter((item) => !item.overdue).length}
              </p>
              <p className="mt-1 text-xs text-[#86868B]">Próximos</p>
            </div>
            <div className="rounded-2xl bg-[#F5F5F7] p-4">
              <ClipboardCheck className="mb-3 h-5 w-5 text-[#34C759]" />
              <p className="font-display text-xl font-bold text-[#1D1D1F]">
                {stats.completadas || 0}
              </p>
              <p className="mt-1 text-xs text-[#86868B]">OTs completadas</p>
            </div>
          </div>

          {(stats.maintenance?.upcoming || []).length > 0 ? (
            <div className="mt-5 space-y-2">
              {(stats.maintenance?.upcoming || []).slice(0, 5).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => navigate(ROUTES.maintenance)}
                  className="flex w-full items-center justify-between gap-4 rounded-xl px-3 py-3 text-left transition-colors hover:bg-[#F5F5F7]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-[#1D1D1F]">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-[#86868B]">
                      {item.client_name}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
                      item.overdue
                        ? "bg-[#FFEBEA] text-[#D70015]"
                        : "bg-[#FFF4E5] text-[#C93400]"
                    }`}
                  >
                    {item.overdue
                      ? `${Math.abs(item.days_until_due)} d vencido`
                      : item.days_until_due === 0
                        ? "Hoy"
                        : `${item.days_until_due} d`}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-5 rounded-2xl bg-[#F5F5F7] p-5 text-center">
              <CheckCircle2 className="mx-auto h-6 w-6 text-[#34C759]" />
              <p className="mt-2 text-sm font-medium text-[#1D1D1F]">Todo al día</p>
              <p className="mt-1 text-xs text-[#86868B]">No hay revisiones próximas.</p>
            </div>
          )}
        </div>
      </section>

      {/* Facturación mensual */}
      <section className="mt-6">
        <div className="mantix-card animate-fade-up p-6">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                Facturación mensual
              </h3>

              <p className="text-sm text-[#86868B]">
                Evolución durante los últimos seis meses
              </p>
            </div>

            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#34C759]/10">
              <TrendingUp className="h-5 w-5 text-[#34C759]" />
            </div>
          </div>

          {revenueSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={revenueSeries}
                margin={{ left: -20, right: 8 }}
              >
                <defs>
                  <linearGradient
                    id="rev"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="#0066CC"
                      stopOpacity={0.25}
                    />

                    <stop
                      offset="100%"
                      stopColor="#0066CC"
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#E8E8ED"
                  vertical={false}
                />

                <XAxis
                  dataKey="month"
                  tick={{
                    fill: "#86868B",
                    fontSize: 12,
                  }}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  tick={{
                    fill: "#86868B",
                    fontSize: 12,
                  }}
                  axisLine={false}
                  tickLine={false}
                />

                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #D2D2D7",
                    fontSize: 13,
                  }}
                  formatter={(value) => [
                    eur(value),
                    "Facturación",
                  ]}
                />

                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#0066CC"
                  strokeWidth={2.5}
                  fill="url(#rev)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[300px] flex-col items-center justify-center rounded-2xl bg-[#F8F8FA] text-center">
              <TrendingUp className="h-8 w-8 text-[#B0B0B5]" />

              <p className="mt-3 text-sm font-medium text-[#1D1D1F]">
                Todavía no hay datos de facturación
              </p>

              <p className="mt-1 max-w-xs text-xs text-[#86868B]">
                La gráfica se actualizará cuando registres órdenes con
                importes.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Prioridad y resumen */}
      <section className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="mantix-card p-6">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                Avisos por prioridad
              </h3>

              <p className="mt-1 text-xs text-[#86868B]">
                Distribución de incidencias activas
              </p>
            </div>

            <Bell className="h-5 w-5 text-[#FF9500]" />
          </div>

          <div className="space-y-4">
            {[
              {
                key: "urgente",
                label: "Urgente",
                color: "#FF3B30",
              },
              {
                key: "alta",
                label: "Alta",
                color: "#FF9500",
              },
              {
                key: "media",
                label: "Media",
                color: "#0066CC",
              },
              {
                key: "baja",
                label: "Baja",
                color: "#86868B",
              },
            ].map((priority) => {
              const value =
                stats.priority_counts?.[priority.key] || 0;

              const percentage =
                (Number(value) / priorityTotal) * 100;

              return (
                <div key={priority.key}>
                  <div className="mb-1.5 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{
                          backgroundColor: priority.color,
                        }}
                      />

                      <span className="text-[#1D1D1F]">
                        {priority.label}
                      </span>
                    </div>

                    <span className="font-mono text-[#86868B]">
                      {value}
                    </span>
                  </div>

                  <div className="h-2 overflow-hidden rounded-full bg-[#F5F5F7]">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: priority.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mantix-card p-6">
          <div className="mb-5">
            <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
              Resumen operativo
            </h3>

            <p className="mt-1 text-xs text-[#86868B]">
              Estado general de la actividad
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              {
                icon: ClipboardCheck,
                label: "Completadas",
                value: stats.completadas || 0,
                color: "#34C759",
              },
              {
                icon: Clock,
                label: "En curso",
                value: stats.en_curso || 0,
                color: "#FF9500",
              },
              {
                icon: Users,
                label: "Clientes",
                value: stats.clients_count || 0,
                color: "#0066CC",
              },
              {
                icon: Bell,
                label: "Avisos totales",
                value: stats.avisos_total || 0,
                color: "#5856D6",
              },
            ].map(({ icon: Icon, label, value, color }) => (
              <div
                key={label}
                className="rounded-2xl border border-transparent bg-[#F5F5F7] p-4 transition-all hover:border-[#E0E0E5] hover:bg-white hover:shadow-sm"
              >
                <Icon
                  className="mb-3 h-5 w-5"
                  style={{ color }}
                  strokeWidth={1.8}
                />

                <p className="font-display text-2xl font-bold text-[#1D1D1F]">
                  {value}
                </p>

                <p className="mt-1 text-xs text-[#86868B]">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Actividad y mapa */}
      <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="mantix-card p-6 lg:col-span-2">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-[#0066CC]" />

              <div>
                <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                  Actividad reciente
                </h3>

                <p className="text-xs text-[#86868B]">
                  Últimos trabajos registrados
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate(ROUTES.workOrders)}
              className="flex items-center gap-1.5 text-sm font-medium text-[#0066CC] hover:underline"
            >
              Ver todas
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {recentOrders.length === 0 ? (
            <div className="flex min-h-44 flex-col items-center justify-center rounded-2xl border border-dashed border-[#D2D2D7] p-6 text-center">
              <ClipboardCheck className="h-8 w-8 text-[#B0B0B5]" />

              <p className="mt-3 text-sm font-medium text-[#1D1D1F]">
                Aún no hay órdenes de trabajo
              </p>

              <p className="mt-1 text-xs text-[#86868B]">
                Crea la primera orden para empezar a registrar la
                actividad.
              </p>

              <button
                type="button"
                onClick={() => navigate(ROUTES.workOrders)}
                className="mt-4 flex items-center gap-2 rounded-full bg-[#0066CC] px-4 py-2 text-sm font-medium text-white hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nueva orden
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              {recentOrders.map((order) => {
                const statusColor =
                  {
                    pendiente: "#86868B",
                    en_curso: "#FF9500",
                    completada: "#34C759",
                    cancelada: "#FF3B30",
                  }[order.status] || "#86868B";

                return (
                  <button
                    type="button"
                    key={order.id}
                    data-testid={`recent-order-${order.id}`}
                    onClick={() => navigate(ROUTES.workOrders)}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-[#F5F5F7]"
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: statusColor,
                      }}
                    />

                    <span className="font-mono text-xs text-[#86868B]">
                      {order.number}
                    </span>

                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#1D1D1F]">
                      {order.title}
                    </span>

                    <span className="hidden max-w-40 truncate text-sm text-[#86868B] sm:block">
                      {order.client_name}
                    </span>

                    <span className="font-mono text-sm font-medium text-[#1D1D1F]">
                      {eur(order.amount || 0)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mantix-card overflow-hidden p-0">
          <div className="flex items-center justify-between p-6 pb-3">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#0066CC]" />

              <div>
                <h3 className="font-display text-lg font-medium text-[#1D1D1F]">
                  Operativa en el mapa
                </h3>

                <p className="text-xs text-[#86868B]">
                  Clientes, técnicos y órdenes
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate(ROUTES.map)}
              className="rounded-lg p-2 text-[#86868B] hover:bg-[#F5F5F7] hover:text-[#0066CC]"
              aria-label="Abrir mapa operativo"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div
            id="mini-map"
            data-testid="dashboard-mini-map"
            className="h-64 w-full bg-[#F5F5F7]"
          />
        </div>
      </section>

      {/* Accesos inferiores */}
      <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            icon: Bell,
            label: "Gestionar avisos",
            description: "Prioriza y asigna incidencias",
            route: ROUTES.alerts,
            color: "#FF9500",
          },
          {
            icon: CalendarDays,
            label: "Abrir calendario",
            description: "Organiza técnicos y trabajos",
            route: ROUTES.calendar,
            color: "#5856D6",
          },
          {
            icon: Receipt,
            label: "Revisar facturas",
            description: "Cobros, vencimientos y estados",
            route: ROUTES.invoices,
            color: "#0066CC",
          },
          {
            icon: Boxes,
            label: "Revisar inventario",
            description: "Stock, materiales y alertas",
            route: ROUTES.inventory,
            color: "#34C759",
          },
        ].map(
          ({
            icon: Icon,
            label,
            description,
            route,
            color,
          }) => (
            <button
              key={label}
              type="button"
              onClick={() => navigate(route)}
              className="mantix-card group flex items-center gap-4 p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
                style={{
                  backgroundColor: `${color}15`,
                }}
              >
                <Icon
                  className="h-5 w-5"
                  style={{ color }}
                  strokeWidth={1.8}
                />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#1D1D1F]">
                  {label}
                </p>

                <p className="mt-0.5 truncate text-xs text-[#86868B]">
                  {description}
                </p>
              </div>

              <ArrowRight className="h-4 w-4 text-[#B0B0B5] transition-transform group-hover:translate-x-0.5 group-hover:text-[#0066CC]" />
            </button>
          )
        )}
      </section>
      {searchOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/30 px-4 pt-[10vh] backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSearchOpen(false);
              setSearchQuery("");
            }
          }}
        >
          <div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-white/60 bg-white shadow-[0_24px_80px_rgba(0,0,0,0.22)]">
            <div className="flex items-center gap-3 border-b border-[#E8E8ED] px-5 py-4">
              <Search className="h-5 w-5 text-[#0066CC]" />

              <input
                autoFocus
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setSearchOpen(false);
                    setSearchQuery("");
                  }

                  if (event.key === "Enter" && searchResults[0]) {
                    openSearchResult(searchResults[0].route);
                  }
                }}
                className="min-w-0 flex-1 bg-transparent text-base text-[#1D1D1F] outline-none placeholder:text-[#A1A1A6]"
                placeholder="Buscar avisos, clientes, técnicos, calendario…"
              />

              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchQuery("");
                }}
                className="rounded-full p-2 text-[#86868B] transition-colors hover:bg-[#F5F5F7] hover:text-[#1D1D1F]"
                aria-label="Cerrar búsqueda"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[420px] overflow-y-auto p-3">
              {searchResults.length > 0 ? (
                searchResults.map((item) => (
                  <button
                    key={`${item.label}-${item.route}`}
                    type="button"
                    onClick={() => openSearchResult(item.route)}
                    className="group flex w-full items-center gap-4 rounded-2xl px-4 py-3 text-left transition-colors hover:bg-[#F5F5F7]"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0066CC]/10">
                      <Search className="h-4 w-4 text-[#0066CC]" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#1D1D1F]">
                        {item.label}
                      </p>

                      <p className="mt-0.5 truncate text-sm text-[#86868B]">
                        {item.description}
                      </p>
                    </div>

                    <ArrowRight className="h-4 w-4 text-[#B0B0B5] transition-transform group-hover:translate-x-0.5 group-hover:text-[#0066CC]" />
                  </button>
                ))
              ) : (
                <div className="px-5 py-12 text-center">
                  <Search className="mx-auto h-8 w-8 text-[#B0B0B5]" />

                  <p className="mt-3 font-medium text-[#1D1D1F]">
                    No encontramos esa sección
                  </p>

                  <p className="mt-1 text-sm text-[#86868B]">
                    Prueba con cliente, aviso, orden, técnico o calendario.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-[#E8E8ED] bg-[#FAFAFC] px-5 py-3 text-xs text-[#86868B]">
              <span>Enter para abrir el primer resultado</span>
              <span>Esc para cerrar</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}